"use client";

import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkFootnotes from "remark-footnotes";
import rehypeHighlight from "rehype-highlight";
import Link from "next/link";
import { formatCategoryLabel, formatBlogDate } from "../lib/formatters";
import { getAbsoluteUrl } from "../lib/url";
import { useLenis } from "./LenisProvider";
import BlogPostsSection from "./BlogPostsSection";
import {
  ArrowLeft,
  Twitter,
  Linkedin,
  Link2,
  Clock,
  Calendar,
  Check,
} from "lucide-react";

interface BlogPostLayoutProps {
  title: string;
  content: string;
  excerpt: string;
  category: string;
  slug: string;
  publishedAt: Date;
  readingTime: number;
  isPreview?: boolean;
}

export default function BlogPostLayout({
  title,
  content,
  excerpt,
  category,
  slug,
  publishedAt,
  readingTime,
  isPreview = false,
}: BlogPostLayoutProps) {
  const shareUrl = getAbsoluteUrl(`/blog/${category}/${slug}`);
  const [copied, setCopied] = useState(false);
  const lenis = useLenis();

  return (
    <article className="min-h-screen bg-[var(--bg-primary)]">
      {/* Hero Section with Featured Image */}
      <div className="relative overflow-hidden bg-[var(--bg-secondary)] mt-6">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-primary)]/5 via-transparent to-[var(--brand-secondary)]/5" />

        {/* Content Container */}
        <div className="relative">
          {/* Top Navigation and Header */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-4 sm:pb-6">
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 text-xs sm:text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors mb-4 sm:mb-6"
            >
              <ArrowLeft size={14} className="sm:w-4 sm:h-4" />
              <span>Back to Blog</span>
            </Link>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] leading-tight mb-3 sm:mb-4">
              {title}
            </h1>

            {/* Excerpt */}
            <p className="text-sm sm:text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mb-4 sm:mb-6 max-w-3xl">
              {excerpt}
            </p>

            {/* Meta Information with Category */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 text-xs sm:text-sm">
              <span className="inline-block px-2.5 sm:px-3 py-1 bg-[var(--bg-tertiary)] text-[var(--text-secondary)] rounded-full font-medium">
                {formatCategoryLabel(category)}
              </span>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-[var(--text-tertiary)]">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Calendar size={14} className="sm:w-4 sm:h-4" />
                  <time dateTime={publishedAt.toISOString()}>
                    {formatBlogDate(publishedAt)}
                  </time>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Clock size={14} className="sm:w-4 sm:h-4" />
                  <span>{readingTime} min read</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="bg-[var(--bg-primary)] relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="bg-[var(--bg-secondary)] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg">

            <div className="prose prose-sm sm:prose-base md:prose-lg lg:prose-xl max-w-none">
              <ReactMarkdown
                remarkPlugins={[
                  remarkGfm
                ]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  // Headings
                  h1: ({ children, ...props }) => {
                    // Extract text content, handling both plain text and {#id} syntax
                    let text = children?.toString() || '';
                    let customId = null;
                    
                    // Check for {#custom-id} pattern
                    const idMatch = text.match(/(.+?)\s*\{#([^}]+)\}/);
                    if (idMatch) {
                      text = idMatch[1].trim();
                      customId = idMatch[2];
                    }
                    
                    // Use custom ID if provided, otherwise generate from text
                    const id = customId || text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    
                    return (
                      <h2
                        id={id}
                        className="text-2xl sm:text-3xl md:text-4xl font-bold text-[var(--text-primary)] mt-8 sm:mt-10 md:mt-12 mb-4 sm:mb-5 md:mb-6 leading-tight scroll-mt-24"
                        {...props}
                      >
                        {text}
                      </h2>
                    );
                  },
                  h2: ({ children, ...props }) => {
                    // Extract text content, handling both plain text and {#id} syntax
                    let text = children?.toString() || '';
                    let customId = null;
                    
                    // Check for {#custom-id} pattern
                    const idMatch = text.match(/(.+?)\s*\{#([^}]+)\}/);
                    if (idMatch) {
                      text = idMatch[1].trim();
                      customId = idMatch[2];
                    }
                    
                    // Use custom ID if provided, otherwise generate from text
                    const id = customId || text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    
                    return (
                      <h3
                        id={id}
                        className="text-xl sm:text-2xl md:text-3xl font-bold text-[var(--text-primary)] mt-6 sm:mt-8 md:mt-10 mb-3 sm:mb-4 md:mb-5 leading-tight scroll-mt-24"
                        {...props}
                      >
                        {text}
                      </h3>
                    );
                  },
                  h3: ({ children, ...props }) => {
                    // Generate ID from heading text
                    const id = children?.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    return (
                      <h4
                        id={id}
                        className="text-lg sm:text-xl md:text-2xl font-semibold text-[var(--text-primary)] mt-6 sm:mt-8 mb-3 sm:mb-4 leading-tight scroll-mt-24"
                        {...props}
                      >
                        {children}
                      </h4>
                    );
                  },
                  h4: ({ children, ...props }) => {
                    // Generate ID from heading text
                    const id = children?.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    return (
                      <h5
                        id={id}
                        className="text-base sm:text-lg md:text-xl font-semibold text-[var(--text-primary)] mt-4 sm:mt-6 mb-2 sm:mb-3 scroll-mt-24"
                        {...props}
                      >
                        {children}
                      </h5>
                    );
                  },

                  // Paragraphs
                  p: ({ children, ...props }) => {
                    // Check if this paragraph is inside a blockquote
                    const isInBlockquote = props.className?.includes('blockquote') || false;
                    
                    return (
                      <p className={`text-[var(--text-secondary)] ${isInBlockquote ? '' : 'mb-4 sm:mb-6'} leading-relaxed text-sm sm:text-base md:text-lg`}>
                        {children}
                      </p>
                    );
                  },


                  // Lists
                  ul: ({ children }) => {
                    let liCounter = 0;
                    return (
                      <ul className="my-4 sm:my-6 space-y-2 sm:space-y-3 text-[var(--text-secondary)]">
                        {React.Children.map(children, (child) => {
                          if (React.isValidElement(child) && child.type === 'li') {
                            liCounter++;
                            return React.cloneElement(child as React.ReactElement<any>, {
                              'data-list-type': 'ul',
                              'data-list-index': liCounter,
                            });
                          }
                          return child;
                        })}
                      </ul>
                    );
                  },
                  ol: ({ children }) => {
                    let liCounter = 0;
                    return (
                      <ol className="my-4 sm:my-6 space-y-2 sm:space-y-3 text-[var(--text-secondary)]">
                        {React.Children.map(children, (child) => {
                          if (React.isValidElement(child) && child.type === 'li') {
                            liCounter++;
                            return React.cloneElement(child as React.ReactElement<any>, {
                              'data-list-type': 'ol',
                              'data-list-index': liCounter,
                            });
                          }
                          return child;
                        })}
                      </ol>
                    );
                  },
                  li: ({ children, ...props }) => {
                    const listType = props['data-list-type'];
                    const listIndex = props['data-list-index'];
                    
                    // Check if this is a footnote list item
                    if (props.id && props.id.startsWith('user-content-fn-')) {
                      return (
                        <li id={props.id} className="text-sm text-[var(--text-secondary)] pl-4 mb-2">
                          <div className="flex items-start gap-2">
                            <span className="text-[var(--brand-primary)] font-semibold min-w-[1.5rem]">
                              {props.id.replace('user-content-fn-', '')}.
                            </span>
                            <div className="flex-1">{children}</div>
                          </div>
                        </li>
                      );
                    }
                    
                    // Regular list items
                    return (
                      <li className="flex items-start text-sm sm:text-base md:text-lg leading-relaxed">
                        <span className="inline-block w-6 text-[var(--brand-primary)] font-bold flex-shrink-0">
                          {listType === 'ol' ? `${listIndex}.` : '•'}
                        </span>
                        <span className="flex-1">{children}</span>
                      </li>
                    );
                  },

                  // Blockquotes
                  blockquote: ({ children }) => (
                    <blockquote className="my-6 sm:my-8 pl-4 sm:pl-6 border-l-4 border-[var(--brand-primary)] bg-gray-800/50 backdrop-blur-sm py-3 sm:py-4 pr-3 sm:pr-4 rounded-r-lg shadow-lg text-[var(--text-secondary)] italic text-sm sm:text-base md:text-lg [&>p]:my-0">
                      {children}
                    </blockquote>
                  ),

                  // Code
                  code: ({ className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || "");
                    // If it's inside a pre tag (code block), don't wrap it
                    if (match) {
                      return (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                    // Inline code
                    return (
                      <code
                        className="px-1 sm:px-1.5 py-0.5 bg-[var(--bg-tertiary)] text-[var(--brand-primary)] rounded text-xs sm:text-sm font-mono"
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="my-4 sm:my-6 p-0 rounded-lg sm:rounded-xl overflow-hidden shadow-lg text-xs sm:text-sm">
                      <div className="bg-gray-900 overflow-x-auto">
                        {children}
                      </div>
                    </pre>
                  ),

                  // Tables
                  table: ({ children }) => (
                    <div className="my-6 sm:my-8 -mx-4 sm:-mx-6 md:mx-0 overflow-x-auto">
                      <div className="inline-block min-w-full align-middle">
                        <table className="min-w-full border border-[var(--border-primary)] divide-y divide-[var(--border-primary)]">
                          {children}
                        </table>
                      </div>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-[var(--bg-tertiary)]">{children}</thead>
                  ),
                  tbody: ({ children }) => (
                    <tbody className="divide-y divide-[var(--border-primary)]">{children}</tbody>
                  ),
                  tr: ({ children }) => (
                    <tr className="hover:bg-[var(--bg-primary)] transition-colors">{children}</tr>
                  ),
                  th: ({ children }) => (
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-[var(--text-primary)] uppercase tracking-wider border-b border-[var(--border-primary)]">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-[var(--text-secondary)] border-b border-[var(--border-primary)]">
                      {children}
                    </td>
                  ),

                  // Images
                  img: ({ src, alt }) => (
                    <figure className="my-6 sm:my-8 -mx-4 sm:-mx-6 md:mx-0">
                      <img
                        src={src}
                        alt={alt}
                        className="w-full rounded-none sm:rounded-lg shadow-xl"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          const figure = target.closest("figure");
                          if (figure) figure.remove();
                        }}
                      />
                      {alt && (
                        <figcaption className="mt-2 sm:mt-3 px-4 sm:px-0 text-center text-xs sm:text-sm text-[var(--text-tertiary)]">
                          {alt}
                        </figcaption>
                      )}
                    </figure>
                  ),

                  // Horizontal Rule
                  hr: () => (
                    <hr className="my-12 border-[var(--border-primary)]" />
                  ),

                  // Footnote Support
                  sup: ({ children, ...props }) => {
                    return (
                      <sup className="text-[var(--brand-primary)] text-xs ml-0.5 font-semibold" {...props}>
                        {children}
                      </sup>
                    );
                  },

                  // Handle footnote reference links (the [1] links in text)
                  a: ({ href, children, ...props }) => {
                    // Check if this is a footnote reference link
                    if (href && href.startsWith('#user-content-fn-')) {
                      const footnoteId = href.replace('#user-content-fn-', '');
                      return (
                        <a
                          href={href}
                          id={`user-content-fnref-${footnoteId}`}
                          className="text-[var(--brand-primary)] hover:text-[var(--brand-secondary)] underline underline-offset-4 decoration-1 hover:decoration-2 transition-all"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    }
                    
                    // Check if this is a footnote back-reference link
                    if (href && href.startsWith('#user-content-fnref-')) {
                      return (
                        <a
                          href={href}
                          className="text-[var(--brand-primary)] hover:text-[var(--brand-secondary)] underline underline-offset-4 decoration-1 hover:decoration-2 transition-all"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    }
                    
                    // Regular links
                    return (
                      <a
                        href={href}
                        className="text-[var(--brand-primary)] hover:text-[var(--brand-secondary)] underline underline-offset-4 decoration-1 hover:decoration-2 transition-all"
                        target={href?.startsWith("http") ? "_blank" : undefined}
                        rel={
                          href?.startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  },

                  // Footnote definitions section
                  section: ({ children, ...props }) => {
                    // Check if this is a footnotes section (data-footnotes is how remark-footnotes marks it)
                    if (props['data-footnotes'] === true) {
                      return (
                        <section className="mt-12 pt-8 border-t border-[var(--border-primary)]" {...props}>
                          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">References</h3>
                          <div className="footnotes space-y-2 text-sm text-[var(--text-secondary)]">
                            {children}
                          </div>
                        </section>
                      );
                    }
                    return <section {...props}>{children}</section>;
                  },
                }}
              >
                {content.replace(/:contentReference\[oaicite:\d+\]\{index=\d+\}/g, '')}
              </ReactMarkdown>
            </div>

            {/* Share Section */}
            {!isPreview && (
              <div className="mt-12 sm:mt-16 pt-8 sm:pt-12 border-t border-[var(--border-primary)]">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-1 sm:mb-2">
                      Share this article
                    </h3>
                    <p className="text-xs sm:text-sm text-[var(--text-tertiary)]">
                      Help others discover this content
                    </p>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-primary)] rounded-lg transition-colors group"
                      aria-label="Share on Twitter"
                    >
                      <Twitter
                        size={16}
                        className="sm:w-[18px] sm:h-[18px] text-[var(--text-tertiary)] group-hover:text-[var(--brand-primary)]"
                      />
                      <span className="text-xs sm:text-sm text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] hidden sm:inline">
                        Twitter
                      </span>
                    </a>

                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-primary)] rounded-lg transition-colors group"
                      aria-label="Share on LinkedIn"
                    >
                      <Linkedin
                        size={16}
                        className="sm:w-[18px] sm:h-[18px] text-[var(--text-tertiary)] group-hover:text-[var(--brand-primary)]"
                      />
                      <span className="text-xs sm:text-sm text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] hidden sm:inline">
                        LinkedIn
                      </span>
                    </a>

                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all group ${
                        copied
                          ? "bg-green-600/20 text-green-500"
                          : "bg-[var(--bg-tertiary)] hover:bg-[var(--bg-primary)] text-[var(--text-secondary)]"
                      }`}
                      aria-label="Copy link"
                    >
                      {copied ? (
                        <>
                          <Check
                            size={16}
                            className="sm:w-[18px] sm:h-[18px]"
                          />
                          <span className="text-xs sm:text-sm hidden sm:inline">
                            Copied!
                          </span>
                        </>
                      ) : (
                        <>
                          <Link2
                            size={16}
                            className="sm:w-[18px] sm:h-[18px] text-[var(--text-tertiary)] group-hover:text-[var(--brand-primary)]"
                          />
                          <span className="text-xs sm:text-sm group-hover:text-[var(--text-primary)] hidden sm:inline">
                            Copy
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blog Posts Section - Show all posts categorized by default */}
      <BlogPostsSection 
        currentCategory={category}
        showRelatedOnly={false}
      />
    </article>
  );
}
